import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TooltipWhenTruncatedDirective } from '@shared/directives/tooltip-when-truncated.directive';

/**
 * A contractor's identity in one grid cell: the account code as a tag, the name over the email.
 *
 * Every BMS screen that names a contractor draws it through here — Contractors, Imports, Reports,
 * Restore — so the same person reads the same way on all four. A screen that knows less of the record
 * (Imports carries neither code nor email) leaves those inputs unset and the cell drops the lines it has
 * nothing for, rather than each screen laying out what it happens to have in its own way.
 *
 * Presentational only: the page maps its own row onto the inputs and owns what a name click does.
 */
@Component({
  selector: 'app-contractor-cell',
  standalone: true,
  imports: [NgbTooltipModule, TooltipWhenTruncatedDirective],
  template: `
    <div class="contractor-cell">
      @if (code) {
        <span
          class="tag tag-dark contractor-cell__code"
          ngbTooltip="Contractor code"
          container="body"
          >{{ code }}</span
        >
      }
      <div class="contractor-cell__text">
        @if (clickable) {
          <button
            type="button"
            class="contractor-cell__name contractor-cell__name--link"
            (click)="nameClick.emit()"
          >
            {{ name }}
          </button>
        } @else {
          <span class="contractor-cell__name">{{ name }}</span>
        }
        @if (email) {
          <span
            class="contractor-cell__email"
            appTooltipWhenTruncated
            [ngbTooltip]="email"
            triggers="manual"
            container="body"
            >{{ email }}</span
          >
        }
      </div>
    </div>
  `,
  styleUrl: './contractor-cell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractorCellComponent {
  /**
   * The contractor's own code, e.g. "DT01" — their handle in Xero and in every import file name. Not the
   * composite account code a billing row carries ("OBMCDT01"): the location half of that belongs to the
   * screen, not to the person the cell names.
   */
  @Input() code: string | null = null;

  @Input({ required: true }) name!: string;

  /** Billing email, under the name. Left unset on a screen whose rows do not carry one. */
  @Input() email: string | null = null;

  /**
   * Make the name the row's handle. A button rather than a clickable span, so it is reachable by
   * keyboard; what it opens is the page's business, so this only reports the click.
   */
  @Input() clickable = false;

  @Output() nameClick = new EventEmitter<void>();
}
